<nav
  class="sidenav"
  [class.sidenav--switcher]="isSpacesSwitcherEnabled$ | async"
  [attr.aria-label]="'Sidebar navigation' | translate"
>
  <div class="sidebar-header">
    <div class="sidebar-header-main">
      <ng-container *ngIf="!isDesktop">
        <div class="sidebar-header-brand">
          <img
            class="sidebar-header-logo"
            [src]="themeService.squareLogoWithFallback$ | async"
            [alt]="sidebarLogoText$ | async"
          />
          <div class="sidebar-logo-text">
            {{ sidebarLogoText$ | async }}
          </div>
        </div>
        <app-pencil-button
          theme="icon"
          icon="close"
          [iconConfig]="{ type: 'material-icon' }"
          class="sidebar-header-close"
          aria-label="Close navigation menu"
          (clickEvent)="closeNavbar()"
        ></app-pencil-button>
      </ng-container>

      <div *ngIf="isDesktop" class="sidebar-logo-title-container">
        <div class="sidebar-logo-text">
          {{ sidebarLogoText$ | async }}
        </div>
      </div>
      <!-- Desktop only: in `over` mode the header's close button already dismisses the sidenav. -->
      <app-nav-sidebar-collapse-button *ngIf="isDesktop"></app-nav-sidebar-collapse-button>
    </div>
  </div>

  <app-nav-sidebar-upgrade-banner *ngIf="isDesktop"></app-nav-sidebar-upgrade-banner>

  <div class="sidebar-top">
    <div class="sidebar-section">
      <app-nav-sidebar-item
        *ngIf="user && visibleNavItems.has(NavBarItems.Home)"
        [label]="'Home' | translate"
        icon="house"
        [active]="active === NavBarItems.Home && !isSettingsClicked"
        dataIntercomTarget="Home"
        (itemClick)="goTo(NavBarItems.Home)"
      ></app-nav-sidebar-item>

      <app-nav-sidebar-item
        *ngIf="
          (isSpacesSwitcherEnabled$ | async) === false && visibleNavItems.has(NavBarItems.Spaces)
        "
        [label]="'Spaces' | translate"
        icon="hash"
        [active]="active === NavBarItems.Spaces && !isSettingsClicked"
        dataIntercomTarget="Whiteboard"
        (itemClick)="goTo(NavBarItems.Spaces)"
      ></app-nav-sidebar-item>

      <app-nav-sidebar-item
        *ngIf="(showControlCenterAlerts$ | async) && visibleNavItems.has(NavBarItems.Alerts)"
        [label]="'Alerts' | translate"
        icon="triangle-alert"
        [active]="active === NavBarItems.Alerts"
        [badge]="navBadgeService.count$(NavBadgeKeys.Alerts) | async"
        dataIntercomTarget="Alerts"
        (itemClick)="goTo(NavBarItems.Alerts)"
      ></app-nav-sidebar-item>

      <app-nav-sidebar-item
        *ngIf="(showScheduleItem$ | async) && visibleNavItems.has(NavBarItems.Schedule)"
        [label]="'Schedule' | translate"
        icon="calendar"
        [active]="active === NavBarItems.Schedule"
        dataIntercomTarget="Schedule"
        (itemClick)="goTo(NavBarItems.Schedule)"
      ></app-nav-sidebar-item>

      <app-nav-sidebar-item
        *ngIf="showSessions$ | async"
        [label]="'Sessions' | translate"
        icon="clipboard-clock"
        [active]="active === NavBarItems.Sessions"
        dataIntercomTarget="Sessions"
        (itemClick)="goTo(NavBarItems.Sessions)"
      ></app-nav-sidebar-item>

      <app-nav-sidebar-item
        *ngIf="(showMessagesItem$ | async) && visibleNavItems.has(NavBarItems.Messages)"
        [label]="'Messages' | translate"
        icon="message-square"
        [active]="active === NavBarItems.Messages"
        dataIntercomTarget="Messages"
        (itemClick)="goTo(NavBarItems.Messages)"
      ></app-nav-sidebar-item>

      <app-nav-sidebar-item
        *ngIf="showPracticeRoleplay$ | async"
        [label]="'Practice Roleplay' | translate"
        icon="graduation-cap"
        [active]="active === NavBarItems.PracticeRoleplay"
        (itemClick)="goTo(NavBarItems.PracticeRoleplay)"
      ></app-nav-sidebar-item>

      <app-nav-sidebar-item
        *ngIf="
          showNotificationsSidebarItem && user && visibleNavItems.has(NavBarItems.Notifications)
        "
        [label]="'Alerts' | translate"
        icon="triangle-alert"
        [active]="active === NavBarItems.Notifications"
        [badge]="notificationsBadge$ | async"
        dataIntercomTarget="Notifications"
        (itemClick)="goTo(NavBarItems.Notifications)"
      ></app-nav-sidebar-item>

      <ng-container *ngIf="isDesktop && (isSpacesSwitcherEnabled$ | async) === false">
        <ng-container *ngTemplateOutlet="toolsItems"></ng-container>
      </ng-container>
    </div>

    <!-- Above the switcher and collapsed by default, so the spaces list gets the sidebar's height. -->
    <app-nav-sidebar-section
      *ngIf="isDesktop && (isSpacesSwitcherEnabled$ | async)"
      class="sidebar-section--tools"
      [label]="'Tools' | translate"
      [expanded]="false"
    >
      <ng-container *ngTemplateOutlet="toolsItems"></ng-container>
    </app-nav-sidebar-section>

    <app-nav-spaces-switcher
      *ngIf="isSpacesSwitcherEnabled$ | async"
      (navigated)="closeNavbar()"
    ></app-nav-spaces-switcher>
  </div>

  <div class="sidebar-bottom">
    <div id="powered-by-pencil" *ngIf="user?.institution && !hidePoweredByPencil">
      <ng-container
        *ngTemplateOutlet="
          customProductName ? customProductNameTemplate : defaultProductNameTemplate
        "
      ></ng-container>
    </div>
  </div>
</nav>

<ng-template #toolsItems>
  <app-nav-sidebar-item
    *ngIf="showCourses$ | async"
    [label]="'Courses' | translate"
    icon="graduation-cap"
    [active]="active === NavBarItems.Courses"
    (itemClick)="goTo(NavBarItems.Courses)"
  ></app-nav-sidebar-item>

  <div *ngIf="showAnalytics$ | async" class="analytics-options">
    <app-nav-sidebar-item
      [label]="'Analytics' | translate"
      icon="chart-no-axes-column-increasing"
      [expanded]="expanded === ExpandedSection.Analytics"
      [showChevron]="true"
      (itemClick)="toggleAnalytics()"
    ></app-nav-sidebar-item>

    <div
      class="sidebar-subsection"
      [class.sidebar-subsection--expanded]="expanded === ExpandedSection.Analytics"
      [attr.aria-hidden]="expanded !== ExpandedSection.Analytics"
      [attr.inert]="expanded === ExpandedSection.Analytics ? null : ''"
    >
      <div class="sidebar-subsection-content">
        <app-nav-sidebar-item
          *ngIf="isSitesEnabled"
          [label]="'Sites' | translate"
          icon="school"
          [active]="active === NavBarItems.Sites"
          [indentationLevel]="1"
          dataIntercomTarget="Sites"
          (itemClick)="goTo(NavBarItems.Sites)"
        ></app-nav-sidebar-item>

        <!-- Gated on teachers_coaching for now, since the Teachers view only
        shows coaching data. -->
        <app-nav-sidebar-item
          *ngIf="showTeachersCoaching$ | async"
          [label]="'Teachers' | translate"
          icon="apple"
          [active]="active === NavBarItems.AnalyticsTeachers"
          [indentationLevel]="1"
          dataIntercomTarget="Teachers"
          (itemClick)="goTo(NavBarItems.AnalyticsTeachers)"
        ></app-nav-sidebar-item>

        <app-nav-sidebar-item
          *ngIf="isAttendanceReportEnabled"
          [label]="'Attendance' | translate"
          icon="calendar"
          [active]="active === NavBarItems.Attendance"
          [indentationLevel]="1"
          dataIntercomTarget="Attendance"
          (itemClick)="goTo(NavBarItems.Attendance)"
        ></app-nav-sidebar-item>

        <app-nav-sidebar-item
          *ngIf="isAnalyticstReportEnabled"
          [label]="'Engagement' | translate"
          icon="chart-no-axes-column-increasing"
          [active]="active === NavBarItems.Engagement"
          [indentationLevel]="1"
          dataIntercomTarget="Engagement"
          (itemClick)="goTo(NavBarItems.Engagement)"
        ></app-nav-sidebar-item>

        <app-nav-sidebar-item
          *ngIf="showRecordings$ | async"
          [label]="'Recordings' | translate"
          icon="circle-play"
          [active]="active === NavBarItems.Recordings"
          [indentationLevel]="1"
          dataIntercomTarget="Recordings"
          (itemClick)="goTo(NavBarItems.Recordings)"
        ></app-nav-sidebar-item>
      </div>
    </div>
  </div>

  <app-nav-sidebar-item
    *ngIf="showPeople$ | async"
    [label]="'People' | translate"
    icon="users"
    [active]="active === NavBarItems.People"
    dataIntercomTarget="Profiles"
    (itemClick)="goTo(NavBarItems.Profiles)"
  ></app-nav-sidebar-item>

  <app-nav-sidebar-item
    *ngIf="showFiles$ | async"
    [label]="'Files' | translate"
    icon="folders"
    [active]="active === NavBarItems.Files"
    dataIntercomTarget="Files-Sidebar"
    (itemClick)="goTo(NavBarItems.Files)"
  ></app-nav-sidebar-item>

  <div *ngIf="showLegacyProfiles$ | async" class="profiles-options">
    <app-nav-sidebar-item
      [label]="'Profiles' | translate"
      icon="users"
      [expanded]="expanded === ExpandedSection.Profiles"
      [showChevron]="true"
      (itemClick)="expanded = expanded === ExpandedSection.Profiles ? '' : ExpandedSection.Profiles"
    ></app-nav-sidebar-item>
    <div
      class="sidebar-subsection"
      [class.sidebar-subsection--expanded]="expanded === ExpandedSection.Profiles"
      [attr.aria-hidden]="expanded !== ExpandedSection.Profiles"
      [attr.inert]="expanded === ExpandedSection.Profiles ? null : ''"
    >
      <div class="sidebar-subsection-content">
        <app-nav-sidebar-item
          [label]="'My profile' | translate"
          icon="users"
          [active]="active === NavBarItems.MyProfile"
          [indentationLevel]="1"
          (itemClick)="goTo(NavBarItems.MyProfile)"
        ></app-nav-sidebar-item>
        <app-nav-sidebar-item
          [label]="'Teachers' | translate"
          icon="apple"
          [active]="active === NavBarItems.Teachers"
          [indentationLevel]="1"
          (itemClick)="goTo(NavBarItems.Teachers)"
        ></app-nav-sidebar-item>
      </div>
    </div>
  </div>

  <app-nav-sidebar-item
    *ngIf="showSettings$ | async"
    [label]="'Settings' | translate"
    icon="settings"
    [active]="active === NavBarItems.Settings || isSettingsClicked"
    dataIntercomTarget="Settings"
    (itemClick)="showSettings()"
  ></app-nav-sidebar-item>

  <div *ngIf="showMoreOptions$ | async" class="more-options">
    <app-nav-sidebar-item
      [label]="'Internal Tools' | translate"
      icon="user-cog"
      [expanded]="expanded === ExpandedSection.More"
      [showChevron]="true"
      (itemClick)="expanded = expanded === ExpandedSection.More ? '' : ExpandedSection.More"
    ></app-nav-sidebar-item>

    <div
      class="sidebar-subsection"
      [class.sidebar-subsection--expanded]="expanded === ExpandedSection.More"
      [attr.aria-hidden]="expanded !== ExpandedSection.More"
      [attr.inert]="expanded === ExpandedSection.More ? null : ''"
    >
      <div class="sidebar-subsection-content">
        <app-nav-sidebar-item
          *ngIf="showAdminPortal$ | async"
          [label]="'Admin Portal' | translate"
          icon="settings"
          [active]="active === NavBarItems.AdminPortal"
          [indentationLevel]="1"
          (itemClick)="goTo(NavBarItems.AdminPortal)"
        ></app-nav-sidebar-item>
        <app-nav-sidebar-item
          *ngIf="showImpersonate$ | async"
          [label]="'Impersonate' | translate"
          icon="users"
          [active]="isImpersonateClicked"
          [indentationLevel]="1"
          (itemClick)="impersonate()"
        ></app-nav-sidebar-item>
        <app-nav-sidebar-item
          *ngIf="showFeatureFlags$ | async"
          [label]="'Feature Flags' | translate"
          icon="triangle-alert"
          [active]="isFeatureFlagsClicked"
          [danger]="isUsingCustomFlags"
          [indentationLevel]="1"
          (itemClick)="showFeatureFlags()"
        ></app-nav-sidebar-item>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #defaultProductNameTemplate>
  {{ 'Powered by ' | translate }}
  <span class="powered-by-product-name">{{ pencilLogoText }}</span>
</ng-template>

<ng-template #customProductNameTemplate>
  <span [innerHTML]="customProductName"></span>
</ng-template>
